import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import AIInsightCard from "../components/AIInsightCard";
import CriticalRoleDrawer from "../components/CriticalRoleDrawer";
import RetentionPanel from "../components/RetentionPanel";
import { fetchEmployees, fetchAttritionRisks, fetchCriticalRoles, fetchHrSummary, fetchRoleInsight } from "../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { AttritionList, SuccessionList, RiskBadge } from "./ManagerDashboard";
import { Users, ShieldCheck, AlertTriangle, GraduationCap, Building2, Sparkles, ShieldAlert, Heart, Zap, FileSearch, Inbox } from "lucide-react";
import MentorRequestsPanel from "../components/MentorRequestsPanel";
import SuggestTrainingDrawer from "../components/SuggestTrainingDrawer";
import SuggestionsOutboxPanel from "../components/SuggestionsOutboxPanel";
import ProactiveSuggestionsWidget from "../components/ProactiveSuggestionsWidget";
import NextActionsCard from "../components/NextActionsCard";
import NineBoxMatrix from "../components/NineBoxMatrix";
import HiPoPoolPanel from "../components/HiPoPoolPanel";
import SuccessionDetailPanel from "../components/SuccessionDetailPanel";
import PromotionCriteriaEditor from "../components/PromotionCriteriaEditor";
import CareerPathEditor from "../components/CareerPathEditor";
import YggCyclesAdmin from "../components/YggCyclesAdmin";
import { HrMentorDashboard } from "../components/MentorProgramComponents";
import { JobPostingsAdmin } from "../components/JobBoardModule";
import RecognitionModule from "../components/RecognitionModule";

export default function HRDashboard() {
  const persona = JSON.parse(sessionStorage.getItem("plena_persona") || "null");
  const nav = useNavigate();
  const loc = useLocation();

  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [attrition, setAttrition] = useState(null);
  const [critical, setCritical] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleInsight, setRoleInsight] = useState(null);
  const [drawerCritId, setDrawerCritId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Suggest training drawer
  const [suggestEmp, setSuggestEmp] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const HR_ID = persona?.employee_id || "emp-017";

  const openSuggestTraining = (emp) => {
    setSuggestEmp(emp);
    setSuggestOpen(true);
  };

  const openCriticalDetail = (critId) => {
    setDrawerCritId(critId);
    setDrawerOpen(true);
  };

  const activeTab = useMemo(() => {
    if (loc.pathname.includes("/talent-pool")) return "pool";
    if (loc.pathname.includes("/nine-box")) return "matrix";
    if (loc.pathname.includes("/hipo")) return "hipo";
    if (loc.pathname.includes("/succession-detail")) return "succession-detail";
    if (loc.pathname.includes("/succession")) return "succession";
    if (loc.pathname.includes("/attrition")) return "attrition";
    if (loc.pathname.includes("/mentor-requests")) return "requests";
    if (loc.pathname.includes("/my-suggestions")) return "outbox";
    if (loc.pathname.includes("/promotion-criteria")) return "promotion";
    if (loc.pathname.includes("/career-paths")) return "paths";
    if (loc.pathname.includes("/ygg")) return "ygg";
    if (loc.pathname.includes("/mentor-pool")) return "mentor";
    if (loc.pathname.includes("/job-postings")) return "jobs";
    if (loc.pathname.includes("/recognitions")) return "recognitions";
    return "overview";
  }, [loc.pathname]);

  useEffect(() => {
    if (!persona) { nav("/"); return; }
    fetchHrSummary().then(setSummary);
    fetchEmployees().then(setEmployees);
    fetchAttritionRisks().then(setAttrition);
    fetchCriticalRoles().then(setCritical);
  }, []);

  useEffect(() => {
    if (!selectedRole) return;
    setRoleInsight(null);
    fetchRoleInsight(selectedRole).then(setRoleInsight);
  }, [selectedRole]);

  const retryRoleInsight = async () => {
    if (!selectedRole) return;
    setRoleInsight(null);
    const d = await fetchRoleInsight(selectedRole);
    setRoleInsight(d);
  };

  if (!persona) return null;

  // Group hierarchy
  const TAB_TO_GROUP = { overview: "org", pool: "org", matrix: "org", hipo: "org", succession: "strategy", "succession-detail": "strategy", attrition: "strategy", requests: "queue", outbox: "queue", promotion: "career", paths: "career", jobs: "career", recognitions: "career", ygg: "career", mentor: "career" };
  const activeGroup = TAB_TO_GROUP[activeTab] || "org";

  return (
    <Layout persona={persona}>
      <div className="mb-8">
        <div className="label-eyebrow text-emerald-600 mb-2">İK Komuta Merkezi</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
          Kurum Yetenek <span className="text-slate-600">Zekası</span>
        </h1>
        <p className="text-base text-slate-600 mt-3 max-w-3xl leading-relaxed">
          Kurumunuzun yetenek havuzunu, kritik roller için yedekleme hazırlığını ve ayrılma risklerini bütüncül izleyin.
        </p>
      </div>

      <Tabs
        value={activeGroup}
        onValueChange={(g) => {
          const firstTab = { org: "overview", strategy: "succession", queue: "requests", career: "promotion" }[g];
          const map = { overview: "/hr", pool: "/hr/talent-pool", succession: "/hr/succession", attrition: "/hr/attrition", requests: "/hr/mentor-requests", outbox: "/hr/my-suggestions", promotion: "/hr/promotion-criteria", jobs: "/hr/job-postings", recognitions: "/hr/recognitions" };
          if (map[firstTab]) nav(map[firstTab]);
        }}
      >
        <TabsList className="bg-white border border-slate-200 mb-6 p-1.5 h-auto flex-wrap">
          <TabsTrigger value="org" data-testid="grp-hr-org" className="text-base px-5 py-2.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
            <Users className="w-4 h-4 mr-1.5" /> Kurum Görünümü
          </TabsTrigger>
          <TabsTrigger value="strategy" data-testid="grp-hr-strategy" className="text-base px-5 py-2.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
            <ShieldAlert className="w-4 h-4 mr-1.5" /> Strateji
          </TabsTrigger>
          <TabsTrigger value="queue" data-testid="grp-hr-queue" className="text-base px-5 py-2.5 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-800 data-[state=active]:shadow-sm">
            <Inbox className="w-4 h-4 mr-1.5" /> Aksiyon Kuyruğu
          </TabsTrigger>
          <TabsTrigger value="career" data-testid="grp-hr-career" className="text-base px-5 py-2.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
            🚀 Kariyer Motoru
          </TabsTrigger>
        </TabsList>

        {/* ============== GROUP: KURUM GÖRÜNÜMÜ ============== */}
        <TabsContent value="org">
          <Tabs
            value={["pool","matrix","hipo"].includes(activeTab) ? activeTab : "overview"}
            onValueChange={(v) => {
              const map = { overview: "/hr", pool: "/hr/talent-pool", matrix: "/hr/nine-box", hipo: "/hr/hipo" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto">
              <TabsTrigger value="overview" data-testid="hr-tab-overview" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Genel Bakış</TabsTrigger>
              <TabsTrigger value="matrix" data-testid="hr-tab-matrix" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">9 Box Matrisi</TabsTrigger>
              <TabsTrigger value="hipo" data-testid="hr-tab-hipo" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">HiPo Havuzu</TabsTrigger>
              <TabsTrigger value="pool" data-testid="hr-tab-pool" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Tüm Çalışanlar</TabsTrigger>
            </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Toplam Çalışan" value={summary?.total_employees ?? "-"} icon={Users} />
            <Kpi label="Aktif Mentor" value={summary?.mentors_active ?? "-"} icon={GraduationCap} />
            <Kpi label="Kritik Rol" value={summary?.critical_role_count ?? "-"} icon={ShieldAlert} accent="amber" />
            <Kpi label="Kurum Ort. Bağlılık" value={summary?.avg_engagement ?? "-"} icon={ShieldCheck} accent="emerald" />
          </div>
          <ProactiveSuggestionsWidget suggesterId={HR_ID} scope="all" />

          <NextActionsCard
            persona={persona}
            role="hr"
            highRiskCount={attrition?.summary?.high || 0}
            attritionTabPath="/hr/attrition"
            mentorTabPath="/hr/mentor-requests"
            outboxTabPath="/hr/my-suggestions"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 card-surface p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="label-eyebrow">Departman Dağılımı</div>
                <Building2 className="w-4 h-4 text-slate-500" />
              </div>
              <div className="space-y-3">
                {summary?.by_department?.map((d) => {
                  const max = Math.max(...summary.by_department.map((x) => x.count));
                  const pct = (d.count / max) * 100;
                  return (
                    <div key={d.name}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-800">{d.name}</span>
                        <span className="font-mono text-slate-600">{d.count}</span>
                      </div>
                      <div className="skill-track"><div className="skill-fill" style={{ width: `${pct}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card-surface p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="label-eyebrow">Risk Özeti</div>
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              <div className="space-y-3">
                <RiskRow level="high" count={attrition?.summary?.high} />
                <RiskRow level="medium" count={attrition?.summary?.medium} />
                <RiskRow level="low" count={attrition?.summary?.low} />
              </div>
              <div className="mt-5 pt-4 border-t border-slate-200">
                <div className="text-[0.7rem] text-slate-500 uppercase tracking-wider mb-1">Toplam İzlenen</div>
                <div className="font-mono text-2xl font-semibold">{attrition?.summary?.total ?? "-"}</div>
              </div>
            </div>
          </div>

          <div className="card-surface p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="label-eyebrow mb-1">Kritik Roller — Yedekleme Hazırlığı</div>
                <h3 className="font-display text-xl font-semibold">Hangi roller risk altında?</h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {critical.map((c) => (
                <button
                  key={c.crit_id || c.role_id}
                  onClick={() => openCriticalDetail(c.crit_id)}
                  data-testid={`critical-role-${c.crit_id || c.role_id}`}
                  className="text-left rounded-xl border border-slate-200 hover:border-sky-400 hover:shadow-md bg-white p-4 transition group"
                >
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <span className="text-sm font-semibold text-slate-900 leading-tight">{c.role_title}</span>
                    <RiskBadge level={c.risk_level} />
                  </div>
                  <div className="text-[0.7rem] text-slate-500 mb-2">{c.department}</div>
                  {c.status_label && (
                    <div className="mb-3">
                      <span className="text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                        {c.status_label}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-emerald-700 font-semibold">{c.ready_now_count}</span>
                    <span className="text-slate-500">hazır</span>
                    <span className="ml-auto font-mono text-slate-700">{c.candidates.length}</span>
                    <span className="text-slate-500">aday</span>
                  </div>
                  <div className="mt-3 text-[0.7rem] text-sky-700 font-medium opacity-0 group-hover:opacity-100 transition">
                    Detay görmek için tıklayın →
                  </div>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pool" className="space-y-3">
          <div className="card-surface p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="label-eyebrow">Yetenek Havuzu — {employees.length} çalışan</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {employees.map((e) => {
                const risk = attrition?.risks?.find((r) => r.employee_id === e.id);
                return (
                  <div key={e.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition" data-testid={`pool-row-${e.id}`}>
                    <Avatar className="w-10 h-10"><AvatarImage src={e.avatar} /><AvatarFallback>{e.name?.charAt(0)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{e.name}</div>
                      <div className="text-[0.7rem] text-slate-500 truncate">{e.title} · {e.department}</div>
                    </div>
                    <div className="hidden md:flex items-center gap-3 text-xs">
                      <div className="text-right">
                        <div className="text-[0.6rem] text-slate-500 uppercase tracking-wider">Perf</div>
                        <div className="font-mono">{e.performance_rating}</div>
                      </div>
                      {e.is_mentor && <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 bg-emerald-500/10 text-[0.6rem]">Mentor</Badge>}
                      {risk && <RiskBadge level={risk.risk_level} />}
                    </div>
                    <button
                      onClick={() => openSuggestTraining(e)}
                      data-testid={`suggest-training-${e.id}`}
                      className="ml-1 text-[0.7rem] px-2.5 py-1 rounded-md border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 inline-flex items-center gap-1 flex-shrink-0"
                    >
                      <Sparkles className="w-3 h-3" /> Gelişim öner
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="matrix" className="space-y-3">
          <NineBoxMatrix />
        </TabsContent>

        <TabsContent value="hipo" className="space-y-3">
          <HiPoPoolPanel />
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ============== GROUP: STRATEJİ ============== */}
        <TabsContent value="strategy">
          <Tabs
            value={["attrition","succession-detail","succession"].includes(activeTab) ? activeTab : "succession"}
            onValueChange={(v) => {
              const map = { succession: "/hr/succession", "succession-detail": "/hr/succession-detail", attrition: "/hr/attrition" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto">
              <TabsTrigger value="succession" data-testid="hr-tab-succession" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Kritik Roller · Yedekleme</TabsTrigger>
              <TabsTrigger value="succession-detail" data-testid="hr-tab-succession-detail" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Ready Now · 1yr · 2-3yr</TabsTrigger>
              <TabsTrigger value="attrition" data-testid="hr-tab-attrition" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Ayrılma Riski</TabsTrigger>
            </TabsList>

        <TabsContent value="succession" className="space-y-6">
          {/* Scenario quick-launchers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="scenario-launchers">
            <ScenarioCard
              icon={ShieldAlert}
              accent="rose"
              tag="SENARYO 1"
              title="Risk Departmanı Yetenek Açığı"
              subtitle="Risk Yöneticisi · 4 ay içinde boşalacak"
              onClick={() => openCriticalDetail("crit-001")}
              testid="scenario-1"
            />
            <ScenarioCard
              icon={Heart}
              accent="amber"
              tag="SENARYO 2"
              title="Kritik Çalışan Elde Tutma"
              subtitle="Elif Şahin · %75 ayrılma riski"
              onClick={() => openCriticalDetail("crit-003")}
              testid="scenario-2"
            />
            <ScenarioCard
              icon={FileSearch}
              accent="sky"
              tag="SENARYO 3"
              title="İç İlan Açıldığında"
              subtitle="Kadıköy Şube Müdürü · 3 aday eşleşti"
              onClick={() => openCriticalDetail("crit-002")}
              testid="scenario-3"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <SuccessionList items={critical} onSelect={openCriticalDetail} />
            </div>
            <div className="space-y-5">
              <div className="card-surface p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-sky-600" />
                  <span className="label-eyebrow">AI Yardım</span>
                </div>
                <p className="text-sm text-slate-600">Bir kritik role tıklayarak detaylı içgörü panelini açabilirsiniz: durum, gerekçe, adaylar, AI yorumu ve maliyet karşılaştırması.</p>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="succession-detail" className="space-y-6">
          <SuccessionDetailPanel />
        </TabsContent>

        <TabsContent value="attrition" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <Kpi label="Yüksek Risk" value={attrition?.summary?.high ?? "-"} icon={AlertTriangle} accent="rose" />
            <Kpi label="Orta Risk" value={attrition?.summary?.medium ?? "-"} icon={AlertTriangle} accent="amber" />
            <Kpi label="Düşük Risk" value={attrition?.summary?.low ?? "-"} icon={ShieldCheck} accent="emerald" />
          </div>
          {/* Elde tutma odak paneli: en yüksek riskli çalışan (Elif Şahin) */}
          {attrition?.risks?.[0] && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-rose-600" />
                <h3 className="font-display text-xl font-semibold">Elde Tutma Odağı — En Yüksek Riskli Çalışan</h3>
              </div>
              <RetentionPanel employeeId={attrition.risks[0].employee_id} />
            </div>
          )}
          <div className="pt-2">
            <div className="label-eyebrow mb-2">Tüm Riskli Çalışanlar</div>
            <AttritionList items={attrition?.risks || []} />
          </div>
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ============== GROUP: AKSİYON KUYRUĞU ============== */}
        <TabsContent value="queue">
          <Tabs
            value={activeTab === "outbox" ? "outbox" : "requests"}
            onValueChange={(v) => {
              const map = { requests: "/hr/mentor-requests", outbox: "/hr/my-suggestions" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto">
              <TabsTrigger value="requests" data-testid="hr-tab-requests" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
                <Inbox className="w-3.5 h-3.5 mr-1.5" /> Mentor Talepleri
              </TabsTrigger>
              <TabsTrigger value="outbox" data-testid="hr-tab-outbox" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-amber-800 data-[state=active]:shadow-sm">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Önerdiklerim
              </TabsTrigger>
            </TabsList>

        <TabsContent value="requests" className="space-y-6">
          <MentorRequestsPanel />
        </TabsContent>

        <TabsContent value="outbox" className="space-y-6">
          <SuggestionsOutboxPanel suggesterId={HR_ID} />
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ============== GROUP: KARİYER MOTORU ============== */}
        <TabsContent value="career">
          <Tabs
            value={["promotion","paths","jobs","recognitions","ygg","mentor"].includes(activeTab) ? activeTab : "promotion"}
            onValueChange={(v) => {
              const map = { promotion: "/hr/promotion-criteria", paths: "/hr/career-paths", jobs: "/hr/job-postings", recognitions: "/hr/recognitions", ygg: "/hr/ygg", mentor: "/hr/mentor-pool" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto flex-wrap">
              <TabsTrigger value="promotion" data-testid="hr-tab-promotion" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">Terfi Kriterleri</TabsTrigger>
              <TabsTrigger value="paths" data-testid="hr-tab-paths" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">Kariyer Yolları</TabsTrigger>
              <TabsTrigger value="ygg" data-testid="hr-tab-ygg" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">YGG Süreci</TabsTrigger>
              <TabsTrigger value="mentor" data-testid="hr-tab-mentor" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">Mentor Havuzu</TabsTrigger>
              <TabsTrigger value="jobs" data-testid="hr-tab-jobs" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">İç İlanlar</TabsTrigger>
              <TabsTrigger value="recognitions" data-testid="hr-tab-recognitions" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">Takdir & Ödül</TabsTrigger>
            </TabsList>

            <TabsContent value="promotion" className="space-y-6">
              <PromotionCriteriaEditor />
            </TabsContent>
            <TabsContent value="paths" className="space-y-6">
              <CareerPathEditor />
            </TabsContent>
            <TabsContent value="ygg" className="space-y-6" data-testid="hr-tabcontent-ygg">
              <YggCyclesAdmin />
            </TabsContent>
            <TabsContent value="mentor" className="space-y-6" data-testid="hr-tabcontent-mentor">
              <HrMentorDashboard />
            </TabsContent>
            <TabsContent value="jobs" className="space-y-6">
              <JobPostingsAdmin />
            </TabsContent>
            <TabsContent value="recognitions" className="space-y-6">
              <RecognitionModule currentEmployeeId={HR_ID} mode="hr" />
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
      <CriticalRoleDrawer critId={drawerCritId} open={drawerOpen} onOpenChange={setDrawerOpen} />
      <SuggestTrainingDrawer open={suggestOpen} onOpenChange={setSuggestOpen} employee={suggestEmp} suggesterId={HR_ID} />
    </Layout>
  );
}

const ScenarioCard = ({ icon: Icon, accent, tag, title, subtitle, onClick, testid }) => {
  const tones = {
    rose: "from-rose-50 to-white border-rose-200 hover:border-rose-400",
    amber: "from-amber-50 to-white border-amber-200 hover:border-amber-400",
    sky: "from-sky-50 to-white border-sky-200 hover:border-sky-400",
  };
  const accents = {
    rose: "text-rose-700",
    amber: "text-amber-700",
    sky: "text-sky-700",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-2xl border bg-gradient-to-br ${tones[accent]} p-5 transition hover:-translate-y-0.5 hover:shadow-md`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${accents[accent]}`} />
        <span className={`label-eyebrow ${accents[accent]}`}>{tag}</span>
      </div>
      <div className="font-display text-base font-semibold text-slate-900 leading-tight mb-1">{title}</div>
      <div className="text-xs text-slate-600">{subtitle}</div>
      <div className={`mt-3 text-[0.7rem] font-medium ${accents[accent]}`}>Senaryoyu aç →</div>
    </button>
  );
};

const Kpi = ({ label, value, icon: Icon, accent = "sky" }) => {
  const map = {
    sky: "text-sky-600 border-sky-500/20",
    amber: "text-amber-600 border-amber-500/20",
    rose: "text-rose-600 border-rose-500/20",
    emerald: "text-emerald-600 border-emerald-500/20",
  };
  return (
    <div className={`card-surface p-6 ${map[accent]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-eyebrow">{label}</span>
        <Icon className="w-4 h-4" strokeWidth={1.7} />
      </div>
      <div className="font-display text-4xl font-semibold">{value}</div>
    </div>
  );
};

const RiskRow = ({ level, count }) => {
  const map = {
    high: { label: "Yüksek Risk", cls: "bg-rose-500/15 border-rose-500/30 text-rose-700" },
    medium: { label: "Orta Risk", cls: "bg-amber-500/15 border-amber-500/30 text-amber-700" },
    low: { label: "Düşük Risk", cls: "bg-emerald-500/15 border-emerald-500/30 text-emerald-700" },
  }[level];
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${map.cls}`}>
      <span className="text-sm font-medium">{map.label}</span>
      <span className="font-mono text-lg">{count ?? "-"}</span>
    </div>
  );
};
